// Firebase Activity Logging Service for Provider Dashboard
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  getDoc,
  getDocs,
  serverTimestamp,
  Timestamp,
  writeBatch,
  limit,
  startAfter,
  DocumentSnapshot
} from 'firebase/firestore';
import { db } from '../../config/firebase';
import {
  ActivityLog,
  ActivityPriority,
  ActivityType,
  HopeConversationSummary,
  HopeMessage,
  DailyActivitySummary,
  ClientActivityProfile,
  ActivityFilters,
  LogActivityRequest,
  ActivitiesCallback,
  ConversationCallback,
  UnsubscribeFunction
} from '../../types/activity';

class FirebaseActivityService {
  private static instance: FirebaseActivityService;
  private activitiesCollection = 'activityLogs';
  private hopeConversationsCollection = 'hopeConversations';
  private hopeMessagesSubcollection = 'messages';
  private dailySummariesCollection = 'dailyActivitySummaries';

  static getInstance(): FirebaseActivityService {
    if (!FirebaseActivityService.instance) {
      FirebaseActivityService.instance = new FirebaseActivityService();
    }
    return FirebaseActivityService.instance;
  }

  // ============ ACTIVITY LOGGING METHODS ============

  /**
   * Log a new activity
   */
  async logActivity(request: LogActivityRequest): Promise<string> {
    try {
      // Determine priority based on activity type and metadata
      const priority = this.determinePriority(request.activityType, request.metadata);

      const activityData = {
        providerId: request.providerId,
        clientId: request.clientId,
        clientName: request.clientName,
        activityType: request.activityType,
        priority,
        title: request.title,
        description: request.description,
        timestamp: serverTimestamp(),
        read: false,
        readAt: null,
        metadata: request.metadata || {}
      };

      const docRef = await addDoc(
        collection(db, this.activitiesCollection),
        activityData
      );

      // Update daily summary (non-critical, may fail silently for clients without write permission)
      this.updateDailySummary(request.providerId, request.activityType, priority);

      return docRef.id;
    } catch (error) {
      console.error('Error logging activity:', error);
      throw error;
    }
  }

  /**
   * Determine priority level based on activity type and metadata
   */
  private determinePriority(
    activityType: ActivityType,
    metadata?: ActivityLog['metadata']
  ): ActivityPriority {
    // Crisis-level activities
    if (activityType === 'crisis_detected') {
      return 'crisis';
    }

    // High priority activities
    if (activityType === 'emergency_cancellation') {
      return 'high';
    }

    if (activityType === 'hope_conversation') {
      // Check sentiment and keywords
      if (metadata?.sentiment === 'concerning') {
        return 'high';
      }
      if (metadata?.keywords?.some(kw =>
        ['sad', 'depressed', 'anxious', 'worried', 'scared'].includes(kw.toLowerCase())
      )) {
        return 'moderate';
      }
    }

    if (activityType === 'form_submitted') {
      // Check for high scores on questionnaires like PHQ-9
      if (metadata?.score && metadata.score >= 15) {
        return 'high';
      }
      if (metadata?.score && metadata.score >= 10) {
        return 'moderate';
      }
    }

    // Moderate priority
    if (['appointment_cancelled', 'message_sent'].includes(activityType)) {
      return 'moderate';
    }

    // Default to routine
    return 'routine';
  }

  /**
   * Subscribe to activities for a provider
   */
  subscribeToActivities(
    providerId: string,
    callback: ActivitiesCallback,
    filters?: ActivityFilters,
    pageSize: number = 50
  ): UnsubscribeFunction {
    let q = query(
      collection(db, this.activitiesCollection),
      where('providerId', '==', providerId),
      orderBy('timestamp', 'desc'),
      limit(pageSize)
    );

    // Apply filters
    if (filters?.clientId) {
      q = query(
        collection(db, this.activitiesCollection),
        where('providerId', '==', providerId),
        where('clientId', '==', filters.clientId),
        orderBy('timestamp', 'desc'),
        limit(pageSize)
      );
    }

    if (filters?.unreadOnly) {
      q = query(
        collection(db, this.activitiesCollection),
        where('providerId', '==', providerId),
        where('read', '==', false),
        orderBy('timestamp', 'desc'),
        limit(pageSize)
      );
    }

    return onSnapshot(q, (snapshot) => {
      let activities = snapshot.docs.map((doc) =>
        this.convertToActivityLog(doc.id, doc.data())
      );

      // Client-side filtering for priorities and types (Firestore doesn't support array filters well)
      if (filters?.priorities && filters.priorities.length > 0) {
        activities = activities.filter(a => filters.priorities!.includes(a.priority));
      }

      if (filters?.activityTypes && filters.activityTypes.length > 0) {
        activities = activities.filter(a => filters.activityTypes!.includes(a.activityType));
      }

      if (filters?.searchQuery) {
        const searchLower = filters.searchQuery.toLowerCase();
        activities = activities.filter(a =>
          a.clientName.toLowerCase().includes(searchLower) ||
          a.title.toLowerCase().includes(searchLower) ||
          a.description.toLowerCase().includes(searchLower)
        );
      }

      callback(activities);
    }, (error) => {
      console.error('Error subscribing to activities:', error);
      callback([]);
    });
  }

  /**
   * Get activities with pagination
   */
  async getActivities(
    providerId: string,
    filters?: ActivityFilters,
    pageSize: number = 50,
    lastDoc?: DocumentSnapshot
  ): Promise<{ activities: ActivityLog[]; lastDoc: DocumentSnapshot | null }> {
    try {
      let q = query(
        collection(db, this.activitiesCollection),
        where('providerId', '==', providerId),
        orderBy('timestamp', 'desc'),
        limit(pageSize)
      );

      if (lastDoc) {
        q = query(
          collection(db, this.activitiesCollection),
          where('providerId', '==', providerId),
          orderBy('timestamp', 'desc'),
          startAfter(lastDoc),
          limit(pageSize)
        );
      }

      const snapshot = await getDocs(q);

      let activities = snapshot.docs.map((doc) =>
        this.convertToActivityLog(doc.id, doc.data())
      );

      // Apply filters
      if (filters?.priorities && filters.priorities.length > 0) {
        activities = activities.filter(a => filters.priorities!.includes(a.priority));
      }

      if (filters?.activityTypes && filters.activityTypes.length > 0) {
        activities = activities.filter(a => filters.activityTypes!.includes(a.activityType));
      }

      const newLastDoc = snapshot.docs.length > 0
        ? snapshot.docs[snapshot.docs.length - 1]
        : null;

      return { activities, lastDoc: newLastDoc };
    } catch (error) {
      console.error('Error getting activities:', error);
      return { activities: [], lastDoc: null };
    }
  }

  /**
   * Mark activity as read
   */
  async markActivityAsRead(activityId: string): Promise<void> {
    try {
      const docRef = doc(db, this.activitiesCollection, activityId);
      await updateDoc(docRef, {
        read: true,
        readAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error marking activity as read:', error);
      throw error;
    }
  }

  /**
   * Mark multiple activities as read
   */
  async markActivitiesAsRead(activityIds: string[]): Promise<void> {
    try {
      const batch = writeBatch(db);

      activityIds.forEach((id) => {
        const docRef = doc(db, this.activitiesCollection, id);
        batch.update(docRef, {
          read: true,
          readAt: serverTimestamp()
        });
      });

      await batch.commit();
    } catch (error) {
      console.error('Error marking activities as read:', error);
      throw error;
    }
  }

  /**
   * Get unread activity count for a provider
   */
  async getUnreadCount(providerId: string): Promise<number> {
    try {
      const q = query(
        collection(db, this.activitiesCollection),
        where('providerId', '==', providerId),
        where('read', '==', false)
      );

      const snapshot = await getDocs(q);
      return snapshot.size;
    } catch (error) {
      console.error('Error getting unread count:', error);
      return 0;
    }
  }

  /**
   * Subscribe to unread activity count
   */
  subscribeToUnreadCount(
    providerId: string,
    callback: (count: number) => void
  ): UnsubscribeFunction {
    const q = query(
      collection(db, this.activitiesCollection),
      where('providerId', '==', providerId),
      where('read', '==', false)
    );

    return onSnapshot(q, (snapshot) => {
      callback(snapshot.size);
    }, (error) => {
      console.error('Error subscribing to unread count:', error);
      callback(0);
    });
  }

  // ============ HOPE CONVERSATION METHODS ============

  /**
   * Start a new Hope conversation session
   */
  async startHopeConversation(
    clientId: string,
    clientName: string,
    providerId: string
  ): Promise<string> {
    try {
      const conversationData = {
        clientId,
        clientName,
        providerId,
        startTime: serverTimestamp(),
        endTime: null,
        duration: 0,
        messageCount: 0,
        sentiment: 'neutral',
        keywords: [],
        crisisDetected: false,
        crisisAlertId: null,
        summary: null,
        flaggedForReview: false,
        reviewedAt: null,
        reviewedBy: null,
        providerNotes: null
      };

      const docRef = await addDoc(
        collection(db, this.hopeConversationsCollection),
        conversationData
      );

      return docRef.id;
    } catch (error) {
      console.error('Error starting Hope conversation:', error);
      throw error;
    }
  }

  /**
   * Add a message to a Hope conversation
   */
  async addHopeMessage(
    conversationId: string,
    message: Omit<HopeMessage, 'id'>
  ): Promise<string> {
    try {
      const messagesRef = collection(
        db,
        this.hopeConversationsCollection,
        conversationId,
        this.hopeMessagesSubcollection
      );

      const docRef = await addDoc(messagesRef, {
        ...message,
        timestamp: serverTimestamp()
      });

      // Update conversation message count
      const conversationRef = doc(db, this.hopeConversationsCollection, conversationId);
      const conversationSnap = await getDoc(conversationRef);
      const currentCount = conversationSnap.data()?.messageCount || 0;

      await updateDoc(conversationRef, {
        messageCount: currentCount + 1
      });

      return docRef.id;
    } catch (error) {
      console.error('Error adding Hope message:', error);
      throw error;
    }
  }

  /**
   * End a Hope conversation session
   */
  async endHopeConversation(
    conversationId: string,
    summary: {
      sentiment: 'positive' | 'neutral' | 'negative' | 'concerning';
      keywords: string[];
      crisisDetected: boolean;
      crisisAlertId?: string;
    }
  ): Promise<void> {
    try {
      const conversationRef = doc(db, this.hopeConversationsCollection, conversationId);
      const conversationSnap = await getDoc(conversationRef);
      const startTime = conversationSnap.data()?.startTime;

      const endTime = new Date();
      const duration = startTime
        ? Math.round((endTime.getTime() - this.timestampToDate(startTime).getTime()) / 60000)
        : 0;

      await updateDoc(conversationRef, {
        endTime: serverTimestamp(),
        duration,
        sentiment: summary.sentiment,
        keywords: summary.keywords,
        crisisDetected: summary.crisisDetected,
        crisisAlertId: summary.crisisAlertId || null,
        flaggedForReview: summary.sentiment === 'concerning' || summary.crisisDetected
      });

      // Log activity for provider
      const conversationData = conversationSnap.data();
      if (conversationData) {
        await this.logActivity({
          providerId: conversationData.providerId,
          clientId: conversationData.clientId,
          clientName: conversationData.clientName,
          activityType: 'hope_conversation',
          title: `${conversationData.clientName} talked with Hope`,
          description: `${conversationData.messageCount + 1} messages, ${duration} min session`,
          metadata: {
            conversationId,
            messageCount: conversationData.messageCount + 1,
            sentiment: summary.sentiment,
            keywords: summary.keywords,
            duration
          }
        });
      }
    } catch (error) {
      console.error('Error ending Hope conversation:', error);
      throw error;
    }
  }

  /**
   * Get Hope conversation details
   */
  async getHopeConversation(conversationId: string): Promise<HopeConversationSummary | null> {
    try {
      const docRef = doc(db, this.hopeConversationsCollection, conversationId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      // Get messages
      const messagesRef = collection(
        db,
        this.hopeConversationsCollection,
        conversationId,
        this.hopeMessagesSubcollection
      );
      const messagesQuery = query(messagesRef, orderBy('timestamp', 'asc'));
      const messagesSnap = await getDocs(messagesQuery);

      const messages = messagesSnap.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        timestamp: this.timestampToDate(doc.data().timestamp)
      })) as HopeMessage[];

      return this.convertToHopeConversation(docSnap.id, docSnap.data(), messages);
    } catch (error) {
      console.error('Error getting Hope conversation:', error);
      return null;
    }
  }

  /**
   * Subscribe to Hope conversations for a provider
   */
  subscribeToHopeConversations(
    providerId: string,
    callback: (conversations: HopeConversationSummary[]) => void,
    limitCount: number = 20
  ): UnsubscribeFunction {
    const q = query(
      collection(db, this.hopeConversationsCollection),
      where('providerId', '==', providerId),
      orderBy('startTime', 'desc'),
      limit(limitCount)
    );

    return onSnapshot(q, async (snapshot) => {
      const conversations = await Promise.all(
        snapshot.docs.map(async (docSnap) => {
          // Get messages for each conversation
          const messagesRef = collection(
            db,
            this.hopeConversationsCollection,
            docSnap.id,
            this.hopeMessagesSubcollection
          );
          const messagesQuery = query(messagesRef, orderBy('timestamp', 'asc'));
          const messagesSnap = await getDocs(messagesQuery);

          const messages = messagesSnap.docs.map((msgDoc) => ({
            id: msgDoc.id,
            ...msgDoc.data(),
            timestamp: this.timestampToDate(msgDoc.data().timestamp)
          })) as HopeMessage[];

          return this.convertToHopeConversation(docSnap.id, docSnap.data(), messages);
        })
      );

      callback(conversations);
    }, (error) => {
      console.error('Error subscribing to Hope conversations:', error);
      callback([]);
    });
  }

  /**
   * Add provider notes to a Hope conversation
   */
  async addProviderNotes(
    conversationId: string,
    notes: string,
    providerId: string,
    providerName: string
  ): Promise<void> {
    try {
      const docRef = doc(db, this.hopeConversationsCollection, conversationId);
      await updateDoc(docRef, {
        providerNotes: notes,
        reviewedAt: serverTimestamp(),
        reviewedBy: providerName
      });
    } catch (error) {
      console.error('Error adding provider notes:', error);
      throw error;
    }
  }

  // ============ DAILY SUMMARY METHODS ============

  /**
   * Update daily summary when activity is logged
   */
  private async updateDailySummary(
    providerId: string,
    activityType: ActivityType,
    priority: ActivityPriority
  ): Promise<void> {
    try {
      const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
      const summaryId = `${providerId}_${today}`;
      const summaryRef = doc(db, this.dailySummariesCollection, summaryId);
      const summarySnap = await getDoc(summaryRef);

      if (summarySnap.exists()) {
        const data = summarySnap.data();
        await updateDoc(summaryRef, {
          totalActivities: (data.totalActivities || 0) + 1,
          [`byPriority.${priority}`]: (data.byPriority?.[priority] || 0) + 1,
          [`byType.${activityType}`]: (data.byType?.[activityType] || 0) + 1
        });
      } else {
        await addDoc(collection(db, this.dailySummariesCollection), {
          id: summaryId,
          date: today,
          providerId,
          totalActivities: 1,
          byPriority: {
            crisis: priority === 'crisis' ? 1 : 0,
            high: priority === 'high' ? 1 : 0,
            moderate: priority === 'moderate' ? 1 : 0,
            routine: priority === 'routine' ? 1 : 0
          },
          byType: {
            [activityType]: 1
          },
          clientsActive: 0,
          conversationsLogged: activityType === 'hope_conversation' ? 1 : 0,
          formsSubmitted: activityType === 'form_submitted' ? 1 : 0,
          appointmentsScheduled: activityType === 'appointment_scheduled' ? 1 : 0,
          crisisAlerts: activityType === 'crisis_detected' ? 1 : 0
        });
      }
    } catch (error) {
      // Silently ignore — clients lack write permission to dailyActivitySummaries
      // This is a provider-side analytics feature, not critical for activity logging
      console.debug('Daily summary update skipped (likely permission):', (error as any)?.code || error);
    }
  }

  /**
   * Get daily summary for a provider
   */
  async getDailySummary(
    providerId: string,
    date: string
  ): Promise<DailyActivitySummary | null> {
    try {
      const summaryId = `${providerId}_${date}`;
      const q = query(
        collection(db, this.dailySummariesCollection),
        where('providerId', '==', providerId),
        where('date', '==', date),
        limit(1)
      );

      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        return null;
      }

      const data = snapshot.docs[0].data();
      return {
        date: data.date,
        providerId: data.providerId,
        totalActivities: data.totalActivities || 0,
        byPriority: data.byPriority || { crisis: 0, high: 0, moderate: 0, routine: 0 },
        byType: data.byType || {},
        clientsActive: data.clientsActive || 0,
        conversationsLogged: data.conversationsLogged || 0,
        formsSubmitted: data.formsSubmitted || 0,
        appointmentsScheduled: data.appointmentsScheduled || 0,
        crisisAlerts: data.crisisAlerts || 0
      };
    } catch (error) {
      console.error('Error getting daily summary:', error);
      return null;
    }
  }

  // ============ CONVERSION HELPERS ============

  private convertToActivityLog(id: string, data: any): ActivityLog {
    return {
      id,
      providerId: data.providerId,
      clientId: data.clientId,
      clientName: data.clientName,
      activityType: data.activityType,
      priority: data.priority,
      title: data.title,
      description: data.description,
      timestamp: this.timestampToDate(data.timestamp),
      read: data.read || false,
      readAt: data.readAt ? this.timestampToDate(data.readAt) : undefined,
      metadata: data.metadata || {}
    };
  }

  private convertToHopeConversation(
    id: string,
    data: any,
    messages: HopeMessage[]
  ): HopeConversationSummary {
    return {
      id,
      clientId: data.clientId,
      clientName: data.clientName,
      providerId: data.providerId,
      startTime: this.timestampToDate(data.startTime),
      endTime: data.endTime ? this.timestampToDate(data.endTime) : undefined,
      duration: data.duration || 0,
      messageCount: data.messageCount || messages.length,
      messages,
      sentiment: data.sentiment || 'neutral',
      keywords: data.keywords || [],
      crisisDetected: data.crisisDetected || false,
      crisisAlertId: data.crisisAlertId,
      summary: data.summary,
      flaggedForReview: data.flaggedForReview || false,
      reviewedAt: data.reviewedAt ? this.timestampToDate(data.reviewedAt) : undefined,
      reviewedBy: data.reviewedBy,
      providerNotes: data.providerNotes
    };
  }

  private timestampToDate(timestamp: Timestamp | Date | null): Date {
    if (!timestamp) return new Date();
    if (timestamp instanceof Timestamp) {
      return timestamp.toDate();
    }
    return timestamp;
  }
}

export default FirebaseActivityService;
